import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Snackbar
} from '@mui/material';
import {
  Add,
  Map,
  Analytics,
  CameraAlt,
  CompareArrows,
  MoreVert,
  Delete,
  CheckCircle,
  Timeline,
  ClearAll,
  Refresh
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useBusiness } from '../../contexts/BusinessContext';
import LocationMap from './LocationMap';
import KeywordSidebar from './KeywordSidebar';
import ComparisonMode from './ComparisonMode';
import RankingProgressOverlay from './RankingProgressOverlay';
import RankingTimeSeries from './RankingTimeSeries';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';

const RankingDashboard = () => {
  const { locationId } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedBusiness } = useBusiness();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [progressKeyword, setProgressKeyword] = useState('');
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [snapshotSuccess, setSnapshotSuccess] = useState(false);
  const [keywordDialog, setKeywordDialog] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [rightPanelView, setRightPanelView] = useState('map'); // map, comparison, timeseries
  const [rankingHistory, setRankingHistory] = useState([]);
  const [simulationMenuAnchor, setSimulationMenuAnchor] = useState(null);
  const [clearingCache, setClearingCache] = useState(false);
  const [karteSnapshotDate, setKarteSnapshotDate] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });

  // Refresh handler - clears cache and reloads all data
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Clear localStorage cache
      const cacheKey = `rankingOverview_${locationId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}_time`);

      // Reload all data with forced refresh
      await loadRankingOverview(true);
      await loadKeywordSuggestions();
      await loadRankingHistory();

      console.log('✅ Ranking data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing ranking data:', error);
    } finally {
      setRefreshing(false);
    }
  }, [locationId]);

  // 🔄 AUTO-REFRESH: Clear cache and load fresh data on page load
  useEffect(() => {
    if (locationId && locationId !== 'undefined') {
      console.log('🔄 AUTO-REFRESH: Clearing cache and loading fresh data...');
      // Clear localStorage cache on component mount
      const cacheKey = `rankingOverview_${locationId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}_time`);

      // Load fresh data
      loadRankingOverview(true); // Force refresh
      loadKeywordSuggestions();
      loadRankingHistory();
    }
  }, [locationId]);

  // 🚀 NEW: React to business selection changes from dropdown
  useEffect(() => {
    if (selectedBusiness && locationId) {
      const businessLocationId = selectedBusiness.locationId || selectedBusiness.id;

      if (businessLocationId === locationId) {
        console.log('🔄 Business context updated in Rankings, ensuring data is fresh:', {
          businessName: selectedBusiness.businessName,
          locationId: businessLocationId
        });

        // Reload all data when business changes
        loadRankingOverview();
        loadRankingHistory();
        loadKeywordSuggestions();
      }
    }
  }, [selectedBusiness, locationId]);

  // Auto-select first keyword when overview changes (only if no keyword is selected)
  useEffect(() => {
    if (overview && overview.trackedKeywords.length > 0) {
      // Check if keyword is specified in URL
      const keywordFromUrl = searchParams.get('keyword');
      if (keywordFromUrl) {
        // Try to find the keyword in trackedKeywords
        const foundKeyword = overview.trackedKeywords.find(k => k.keyword === decodeURIComponent(keywordFromUrl));
        if (foundKeyword) {
          setSelectedKeyword(foundKeyword.keyword);
          console.log('🔄 Selected keyword from URL:', foundKeyword.keyword);
          return;
        }
      }

      // Only auto-select the first keyword if:
      // 1. No keyword is currently selected, OR
      // 2. The currently selected keyword no longer exists in the overview
      if (!selectedKeyword || !overview.trackedKeywords.find(k => k.keyword === selectedKeyword)) {
        setSelectedKeyword(overview.trackedKeywords[0].keyword);
        console.log('🔄 Auto-selected first keyword:', overview.trackedKeywords[0].keyword);
      }
    }
  }, [overview, searchParams]);

  const loadRankingOverview = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);

      // Check localStorage cache (1 hour TTL) if not forcing refresh
      if (!forceRefresh) {
        const cacheKey = `rankingOverview_${locationId}`;
        const cached = localStorage.getItem(cacheKey);
        const cachedTime = localStorage.getItem(`${cacheKey}_time`);
        const ONE_HOUR = 60 * 60 * 1000;

        if (cached && cachedTime && Date.now() - parseInt(cachedTime) < ONE_HOUR) {
          setOverview(JSON.parse(cached));
          setLoading(false);
          return;
        }
      } else {
        // Clear cache when forcing refresh
        const cacheKey = `rankingOverview_${locationId}`;
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_time`);
      }

      const response = await axios.get(`/api/businesses/${locationId}/ranking-overview`);
      if (response.data.success) {
        setOverview(response.data.data);
        // Cache in localStorage for 1 hour
        const cacheKey = `rankingOverview_${locationId}`;
        localStorage.setItem(cacheKey, JSON.stringify(response.data.data));
        localStorage.setItem(`${cacheKey}_time`, Date.now().toString());

        // 🔄 AUTO-IMPORT: Log successful loading of potentially auto-imported business
        if (response.data.data && response.data.data.businessName) {
          console.log(`✅ RANKING: Successfully loaded data for ${response.data.data.businessName}`);
        }
      }
    } catch (error) {
      console.error('Error loading ranking overview:', error);

      // 🔄 AUTO-IMPORT: Enhanced error handling for new businesses
      if (error.response?.status === 404) {
        console.log('📝 RANKING: Business not found - auto-import should have been attempted');
        // Note: Auto-import happens in backend, so we just log here
      }
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  const loadKeywordSuggestions = useCallback(async () => {
    try {
      const response = await axios.get(`/api/businesses/${locationId}/keyword-suggestions`);
      if (response.data.success) {
        // Handle new structure with categorized suggestions
        const suggestions = response.data.data.suggestions || response.data.data;
        setKeywordSuggestions(Array.isArray(suggestions) ? suggestions : []);
      }
    } catch (error) {
      console.error('Error loading keyword suggestions:', error);
    }
  }, [locationId]);

  const loadRankingHistory = useCallback(async () => {
    try {
      console.log(`🔍 RANKING HISTORY: Loading for location ${locationId}`);
      const response = await axios.get(`/api/businesses/${locationId}/ranking-history`);
      
      console.log('📡 RANKING HISTORY API Response:', {
        success: response.data.success,
        dataStructure: Object.keys(response.data),
        hasSnapshots: !!(response.data.data?.snapshots),
        hasKeywordHistory: !!(response.data.data?.keywordHistory),
        snapshotsLength: response.data.data?.snapshots?.length || 0
      });
      
      if (response.data.success) {
        // CRITICAL FIX: Transform keywordHistory to snapshots format for comparison mode
        let snapshots = response.data.data?.snapshots || response.data.snapshots || [];
        
        // If no snapshots but keywordHistory exists, transform it
        if (snapshots.length === 0 && response.data.data?.keywordHistory) {
          console.log('🔧 RANKING HISTORY: Converting keywordHistory to snapshots format');
          const keywordHistory = response.data.data.keywordHistory;
          
          // Create a map to group entries by date
          const dateGroups = {};
          
          Object.keys(keywordHistory).forEach(keyword => {
            keywordHistory[keyword].forEach(entry => {
              // Use date string without time to group by day
              const dateKey = entry.date.split('T')[0]; // "2025-08-19" format
              
              if (!dateGroups[dateKey]) {
                dateGroups[dateKey] = {
                  snapshotDate: entry.date, // Keep original full date
                  analyzedAt: entry.date, // For compatibility
                  keywords: [],
                  isSimulation: false
                };
              }
              
              // Transform entry to keyword format expected by SplitMapComparison
              const keywordData = {
                keyword: keyword,
                averageRank: entry.averageRank,
                testLocations: entry.locations.map(loc => ({
                  locationName: loc.locationName,
                  lat: loc.coordinates?.lat,
                  lng: loc.coordinates?.lng,
                  rank: loc.rank
                }))
              };
              
              dateGroups[dateKey].keywords.push(keywordData);
            });
          });
          
          console.log('🔧 RANKING HISTORY: Date groups created:', Object.keys(dateGroups));
          
          // Convert to array and sort by date
          snapshots = Object.values(dateGroups).sort((a, b) => 
            new Date(a.snapshotDate) - new Date(b.snapshotDate)
          );
          
          console.log(`🔧 RANKING HISTORY: Converted ${Object.keys(keywordHistory).length} keywords into ${snapshots.length} snapshots`);
        }
        
        console.log(`✅ RANKING HISTORY: Loaded ${snapshots.length} snapshots`);
        setRankingHistory(snapshots);
        console.log('---snapshots',snapshots)
      }
    } catch (error) {
      console.error('❌ Error loading ranking history:', error);
      console.error('Response data:', error.response?.data);
    }
  }, [locationId]);

  const analyzeKeyword = async (keyword) => {
    try {
      setAnalyzing(true);
      setProgressKeyword(keyword);
      setProgressData({ phase: 'starting', currentPin: 0, totalPins: 18, message: 'Initialisiere...' });
      
      const response = await axios.post(`/api/businesses/${locationId}/analyze-keyword`, {
        keyword,
        priority: 'secondary'
      });
      
      if (response.data.success) {
        console.log('✅ Keyword added successfully, reloading data...');

        // Show toast notification
        setToast({
          open: true,
          message: `Keyword "${keyword}" hinzugefügt`,
          type: 'success'
        });

        // Reload overview with forceRefresh=true to bypass cache and call handleRefresh
        await handleRefresh();

        // Auto-select the analyzed keyword for map display
        setSelectedKeyword(keyword);
        setKeywordDialog(false);
        setNewKeyword('');
      } else {
        // Handle unsuccessful response
        console.error('Keyword analysis failed:', response.data);
        alert('Keyword-Analyse fehlgeschlagen. Versuchen Sie es erneut.');
      }
    } catch (error) {
      console.error('Error during keyword analysis:', error);
      
      // Enhanced error handling with better user messages
      let errorMessage = 'Keyword-Analyse fehlgeschlagen';
      
      if (error.response?.data?.userMessage) {
        errorMessage = error.response.data.userMessage;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 500) {
        errorMessage = 'Server-Fehler. Bitte versuchen Sie es später erneut.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
      } else if (error.message?.includes('Network Error')) {
        errorMessage = 'Netzwerk-Fehler. Überprüfen Sie Ihre Internetverbindung.';
      }
      
      alert(errorMessage);
    } finally {
      setAnalyzing(false);
      setProgressData(null);
    }
  };

  

  const createSnapshot = async () => {
    if (!selectedKeyword) {
      alert('Bitte wählen Sie zuerst ein Keyword aus.');
      return;
    }

    setCreatingSnapshot(true);
    setSnapshotSuccess(false);
    
    try {
      console.log(`📸 Creating snapshot for keyword: ${selectedKeyword}`);
      
      const response = await axios.post(`/api/businesses/${locationId}/create-snapshot`, {
        notes: `Neue Messung für "${selectedKeyword}"`,
        selectedKeyword: selectedKeyword
      });
      
      console.log('📊 Snapshot API Response:', response.data);
      
      if (response.data.success) {
        console.log('✅ Snapshot creation successful');
        
        // Show success state immediately
        setSnapshotSuccess(true);
        
        // Refresh all data to show updated snapshots
        console.log('🔄 Refreshing UI data...');
        await Promise.all([
          loadRankingOverview(),
          loadRankingHistory()
        ]);
        
        console.log('✅ UI data refreshed');
        
        // Show success message with snapshot info
        const totalSnapshots = response.data.data?.totalSnapshots || 'Unknown';
        const cacheCleared = response.data.data?.cacheCleared;
        
        // Zeige Validierungswarnungen an, falls vorhanden
        const warnings = response.data.data?.snapshot?.validationWarnings || response.data.data?.validationWarnings || [];
        if (warnings.length > 0) {
          alert(`✅ Messung erfolgreich erstellt!\n📊 Total Snapshots: ${totalSnapshots}\n${cacheCleared ? '🔄 Cache aktualisiert' : ''}\n\nHinweise:\n${warnings.join('\n')}`);
        } else {
          alert(`✅ Ranking-Messung für "${selectedKeyword}" erfolgreich erstellt!\n📊 Total Snapshots: ${totalSnapshots}${cacheCleared ? '\n🔄 Cache aktualisiert' : ''}`);
        }
        
        // Keep success icon visible for 3 seconds
        setTimeout(() => {
          setSnapshotSuccess(false);
        }, 3000);
        
      } else {
        // API returned but success was false
        console.error('Snapshot creation failed:', response.data);
        setSnapshotSuccess(false);
        alert(`❌ Fehler beim Durchführen der Messung: ${response.data.error || 'Unbekannter Fehler'}`);
      }
      
    } catch (error) {
      console.error('Error creating snapshot:', error);
      setSnapshotSuccess(false);
      
      // More detailed error information
      if (error.response) {
        console.error('API Error Details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        
        if (error.response.status === 500) {
          // Check if snapshot was still created despite error
          const errorMessage = error.response.data?.error || 'Interner Serverfehler';
          console.log('🔍 Checking if snapshot was created despite 500 error...');
          
          // Refresh data to check if snapshot exists
          try {
            await loadRankingHistory();
            alert(`⚠️ Server-Fehler aufgetreten, aber Snapshot möglicherweise trotzdem erstellt.\nFehler: ${errorMessage}\n\n✅ Daten wurden aktualisiert. Prüfen Sie die Snapshot-Liste.`);
          } catch (refreshError) {
            alert(`❌ Server-Fehler (500): ${errorMessage}`);
          }
        } else if (error.response.status === 404) {
          alert('❌ Business-Profil nicht gefunden (404)');
        } else if (error.response.status === 401) {
          alert('❌ Authentifizierung fehlgeschlagen (401)');
        } else {
          alert(`❌ Fehler beim Durchführen der Messung (${error.response.status}): ${error.response.data?.error || error.message}`);
        }
      } else if (error.request) {
        alert('❌ Netzwerkfehler: Server nicht erreichbar');
      } else {
        alert(`❌ Fehler beim Durchführen der Messung: ${error.message}`);
      }
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const deleteKeyword = async (keyword) => {
    if (!window.confirm(`Sind Sie sicher, dass Sie das Keyword "${keyword}" löschen möchten?`)) {
      return;
    }

    try {
      const response = await axios.delete(`/api/businesses/${locationId}/keywords/${encodeURIComponent(keyword)}`);

      if (response.data.success) {
        // Show toast notification
        setToast({
          open: true,
          message: `Keyword "${keyword}" gelöscht`,
          type: 'success'
        });

        // Call handleRefresh to reload all data
        await handleRefresh();

        // Wenn gelöschtes Keyword ausgewählt war, wähle ein anderes
        if (selectedKeyword === keyword) {
          setSelectedKeyword(null);
        }
      }
    } catch (error) {
      console.error('Error deleting keyword:', error);
      alert('Fehler beim Löschen des Keywords.');
    }
  };

  const handleKeywordSelect = (keyword) => {
    setSelectedKeyword(keyword);
    // Will be auto-set to latest when karteAvailableDates updates
  };

  const handleKeywordRefresh = (keyword) => {
    analyzeKeyword(keyword);
  };

  const generateSimulation = async () => {
    if (!window.confirm('Möchten Sie 5 Wochen historische Ranking-Daten simulieren? Dies erstellt Testdaten mit verbesserlichen Trends.')) {
      return;
    }

    try {
      setAnalyzing(true);
      const response = await axios.post(`/api/businesses/${locationId}/simulate-ranking-history`, {
        weeks: 5,
        improvementRate: 0.15 // 15% Verbesserungsrate
      });
      
      if (response.data.success) {
        await loadRankingOverview();
        await loadRankingHistory();
        alert(`✅ ${response.data.data.simulatedSnapshots} Wochen Simulations-Daten generiert! Prüfen Sie jetzt den Vergleichsmodus.`);
      }
    } catch (error) {
      console.error('Error generating simulation:', error);
      alert('Fehler beim Generieren der Simulation.');
    } finally {
      setAnalyzing(false);
    }
  };
// refershes all keyword analysis
const refreshAllKeywords = async () => {
    if (!window.confirm('Alle Keywords analysieren und Snapshot erstellen? Dies kann einige Minuten dauern.')) {
      return;
    }

    try {
      setAnalyzing(true);
      
      // Get all keywords for this business
      if (!overview?.trackedKeywords || overview.trackedKeywords.length === 0) {
        alert('Keine Keywords zum Analysieren vorhanden.');
        return;
      }

      const keywords = overview.trackedKeywords.map(k => k.keyword);
      
      // Analyze all keywords sequentially
      for (let i = 0; i < keywords.length; i++) {
        const keyword = keywords[i];
        setProgressKeyword(keyword);
        
        console.log(`🔍 Analyzing keyword ${i + 1}/${keywords.length}: ${keyword}`);
        
        const response = await axios.post(`/api/businesses/${locationId}/analyze-keyword`, {
          keyword: keyword,
          priority: 'secondary',
        
        });

        if (response.data.success) {
          console.log(`✅ Keyword ${keyword} analyzed successfully`);
        } else {
          console.error(`❌ Keyword ${keyword} analysis failed:`, response.data.error);
        }

        // Small delay between keyword analyses
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      // Reload data after all analyses complete
      await loadRankingOverview();
      await loadRankingHistory();
      
      // Refresh cache to ensure latest snapshots are available
      try {
        const cacheResponse = await axios.post(`/api/businesses/${locationId}/refresh-cache`, {
          type: 'ranking-snapshots'
        });

        if (cacheResponse.data.success) {
          console.log('✅ Cache refreshed successfully');
        }
      } catch (cacheError) {
        console.error('❌ Error refreshing cache:', cacheError);
      }
      
      alert(`✅ Alle ${keywords.length} Keywords erfolgreich analysiert, Snapshot erstellt! und cache refreshed`);
      
    } catch (error) {
      console.error('Error refreshing all keywords:', error);
      alert('Fehler beim Analysieren der Keywords.');
    } finally {
      setAnalyzing(false);
      setProgressKeyword('');
    }
  };

  const clearSimulation = async () => {
    if (!window.confirm('Möchten Sie alle Simulations-Daten entfernen?')) {
      return;
    }

    try {
      setAnalyzing(true);
      const response = await axios.delete(`/api/businesses/${locationId}/clear-simulation`);
      
      if (response.data.success) {
        await loadRankingOverview();
        await loadRankingHistory();
        alert(`✅ ${response.data.data.removedSnapshots} Simulations-Snapshots entfernt.`);
      }
    } catch (error) {
      console.error('Error clearing simulation:', error);
      alert('Fehler beim Entfernen der Simulation.');
    } finally {
      setAnalyzing(false);
    }
  };

  const clearCache = async () => {
    if (!window.confirm('Cache für dieses Business löschen? Dies erzwingt das Neuladen aller Daten beim nächsten Aufruf.')) {
      return;
    }

    try {
      setClearingCache(true);
      console.log(`🧹 Clearing cache for business: ${locationId}`);
      
      const response = await axios.delete(`/api/businesses/${locationId}/clear-cache`);
      
      if (response.data.success) {
        console.log(`✅ Cache cleared: ${response.data.keysDeleted} keys deleted`);
        
        // Reload all data to show fresh results
        await Promise.all([
          loadRankingOverview(),
          loadRankingHistory(),
          loadKeywordSuggestions()
        ]);
        
        alert(`✅ Cache erfolgreich gelöscht!\n📊 ${response.data.keysDeleted} Cache-Einträge entfernt.\n🔄 Daten wurden neu geladen.`);
      } else {
        alert(`⚠️ ${response.data.message}\n${response.data.warning || ''}`);
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      
      let errorMessage = 'Fehler beim Löschen des Caches.';
      if (error.response?.status === 404) {
        errorMessage = 'Business nicht gefunden.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
      } else if (error.response?.data?.error) {
        errorMessage = `Cache-Fehler: ${error.response.data.error}`;
      }
      
      alert(errorMessage);
    } finally {
      setClearingCache(false);
    }
  };

  const getSelectedKeywordData = () => {
    if (!selectedKeyword || !overview?.trackedKeywords) {
      return null;
    }
    return overview.trackedKeywords.find(k => k.keyword === selectedKeyword);
  };

  // Compute available snapshot dates for the Karte tab
  const karteAvailableDates = useMemo(() => {
    if (!selectedKeyword || !rankingHistory?.length) {
      return [];
    }

    // Group snapshots by day, keeping latest per day with the selected keyword
    const dayGroups = {};

    rankingHistory.forEach(entry => {
      const dateValue = entry.analyzedAt || entry.snapshotDate;
      const dateObj = new Date(dateValue);

      if (dateObj instanceof Date && !isNaN(dateObj)) {
        const dayKey = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD

        // Check if snapshot contains the selected keyword
        let keywordInSnapshot = entry.keywords?.find(k => k.keyword === selectedKeyword);
        let keywordInMetrics = entry.keywordMetrics?.find(k => k.keyword === selectedKeyword);

        // Fallback to case-insensitive match
        if (!keywordInSnapshot) {
          keywordInSnapshot = entry.keywords?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
        }
        if (!keywordInMetrics) {
          keywordInMetrics = entry.keywordMetrics?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
        }

        const hasKeyword = !!(keywordInSnapshot || keywordInMetrics);

        if (hasKeyword) {
          if (!dayGroups[dayKey]) {
            dayGroups[dayKey] = [];
          }
          dayGroups[dayKey].push({ dateValue, dateObj, entry });
        }
      }
    });

    // For each day, select the latest snapshot
    const dates = Object.keys(dayGroups)
      .map(dayKey => {
        const daySnapshots = dayGroups[dayKey].sort((a, b) => new Date(b.dateValue) - new Date(a.dateValue));
        const latest = daySnapshots[0];
        return {
          value: latest.dateValue,
          label: latest.dateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }),
          data: latest.entry
        };
      })
      .sort((a, b) => new Date(a.value) - new Date(b.value));

    return dates;
  }, [selectedKeyword, rankingHistory]);

  // Compute keyword data for Karte tab based on selected snapshot date
  const karteKeywordData = useMemo(() => {
    if (!selectedKeyword) {
      return null;
    }

    // If no snapshot date selected yet (initial state), use overview data
    if (!karteSnapshotDate) {
      if (!overview?.trackedKeywords) {
        return null;
      }
      const overviewKeyword = overview.trackedKeywords.find(k => k.keyword === selectedKeyword);
      // Ensure keyword data has testLocations for map display
      if (overviewKeyword) {
        return {
          ...overviewKeyword,
          testLocations: overviewKeyword.testLocations || []
        };
      }
      return null;
    }

    // Find snapshot by date and extract keyword data
    const selectedSnapshot = rankingHistory.find(entry => {
      const dateValue = entry.analyzedAt || entry.snapshotDate;
      return new Date(dateValue).getTime() === new Date(karteSnapshotDate).getTime();
    });

    if (!selectedSnapshot) {
      return null;
    }

    // Extract keyword data from snapshot
    let keywordData = selectedSnapshot.keywords?.find(k => k.keyword === selectedKeyword) ||
                      selectedSnapshot.keywordMetrics?.find(k => k.keyword === selectedKeyword);

    // Fallback to case-insensitive match
    if (!keywordData) {
      keywordData = selectedSnapshot.keywords?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase()) ||
                    selectedSnapshot.keywordMetrics?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
    }

    if (!keywordData) {
      return null;
    }

    // Ensure keyword data has testLocations for map display
    return {
      ...keywordData,
      lastAnalyzed: selectedSnapshot.analyzedAt || selectedSnapshot.snapshotDate,
      testLocations: keywordData.testLocations || []
    };
  }, [karteSnapshotDate, selectedKeyword, overview, rankingHistory]);

  // Auto-select latest available snapshot date
  useEffect(() => {
    if (karteAvailableDates.length > 0) {
      const latestDate = karteAvailableDates[karteAvailableDates.length - 1].value;
      setKarteSnapshotDate(latestDate);
    } else {
      // If no snapshots available, reset snapshot date to use overview data
      setKarteSnapshotDate('');
    }
  }, [karteAvailableDates]);

  // Reload overview when a new keyword is selected to ensure fresh data
  useEffect(() => {
    if (selectedKeyword && !rankingHistory.some(entry =>
      (entry.keywords?.some(k => k.keyword === selectedKeyword) ||
       entry.keywordMetrics?.some(k => k.keyword === selectedKeyword))
    )) {
      // If selected keyword is not in ranking history, reload overview to get latest data
      console.log(`🔄 Selected keyword "${selectedKeyword}" not in history, reloading overview...`);
      loadRankingOverview();
    }
  }, [selectedKeyword, rankingHistory, loadRankingOverview]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!locationId || locationId === 'undefined') {
    return (
      <Alert severity="error">
        Ungültige Business-ID. Bitte wählen Sie ein gültiges Business aus der Liste aus.
      </Alert>
    );
  }

  if (!overview) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Ranking-Daten. Stellen Sie sicher, dass Ihr Business-Profil korrekt konfiguriert ist.
      </Alert>
    );
  }


  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: amDesignColors.paper
    }}>
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${amDesignColors.border}`,
          backgroundColor: amDesignColors.paper
        }}
      >
        {/* Main Row: Title on Left, All Controls on Right */}
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={3}>
          {/* Left: Title & Subtitle */}
          <Box>
            <Typography
              variant="h1"
              sx={{
                mb: 0.25,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: amDesignColors.text.primary,
                fontFamily: amDesignTypography.fontFamily.heading,
                fontSize: '1.8rem'
              }}
            >
              Wo Sie lokal gefunden werden
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: amDesignColors.text.secondary,
                fontFamily: amDesignTypography.fontFamily.body,
                fontSize: '0.9rem'
              }}
            >
              {overview?.businessName} • gemessen an 19 Punkten • Stand {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>

          {/* Right: All Controls in One Row */}
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
            {/* Refresh Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              startIcon={<Refresh sx={{ fontSize: '0.9rem' }} />}
              sx={{
                ...amDesignComponents.button,
                backgroundColor: amDesignColors.white,
                border: `1px solid ${amDesignColors.border}`,
                color: amDesignColors.text.primary,
                fontSize: '0.85rem',
                py: 0.6,
                px: 1.5,
                '&:hover': {
                  backgroundColor: amDesignColors.paper,
                  borderColor: amDesignColors.primary,
                  color: amDesignColors.primary
                }
              }}
            >
              Aktualisieren
            </Button>

            {/* Refresh All Keywords Button */}
            <Button
              variant="contained"
              startIcon={<Analytics sx={{ fontSize: '0.9rem' }} />}
              onClick={refreshAllKeywords}
              disabled={analyzing}
              size="small"
              sx={{
                ...amDesignComponents.button,
                px: 1.5,
                py: 0.6,
                fontSize: '0.85rem',
                backgroundColor: amDesignColors.primary,
                color: amDesignColors.white,
                '&:hover': { backgroundColor: amDesignColors.primaryDark }
              }}
            >
              Refresh all keywords
            </Button>

            {/* Clear Cache Button */}
            <Button
              variant="outlined"
              startIcon={clearingCache ? (
                <CircularProgress size="0.9rem" />
              ) : (
                <ClearAll sx={{ fontSize: '0.9rem' }} />
              )}
              onClick={clearCache}
              disabled={analyzing || clearingCache}
              size="small"
              sx={{
                ...amDesignComponents.button,
                px: 1.5,
                py: 0.6,
                fontSize: '0.85rem',
                backgroundColor: amDesignColors.white,
                border: `1px solid ${amDesignColors.border}`,
                color: amDesignColors.text.primary,
                '&:hover': {
                  backgroundColor: amDesignColors.paper,
                  borderColor: amDesignColors.primary,
                  color: amDesignColors.primary
                }
              }}
            >
              {clearingCache ? 'Clearing...' : 'Cache löschen'}
            </Button>

            {/* Tab-style Toggle */}
            <Box
              sx={{
                display: 'inline-flex',
                backgroundColor: '#EFEFEF',
                borderRadius: '8px',
                padding: '4px',
                gap: 0.5
              }}
            >
              {[
                { value: 'map', label: 'Karte' },
                { value: 'timeseries', label: 'Zeitverlauf' },
                { value: 'comparison', label: 'Vergleich' }
              ].map(tab => {
                const isSelected = rightPanelView === tab.value;
                return (
                  <Button
                    key={tab.value}
                    onClick={() => setRightPanelView(tab.value)}
                    sx={{
                      px: 1.25,
                      py: 0.5,
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      borderRadius: '6px',
                      backgroundColor: isSelected ? amDesignColors.white : 'transparent',
                      color: amDesignColors.text.primary,
                      border: 'none',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: isSelected ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                      '&:hover': {
                        backgroundColor: isSelected ? amDesignColors.white : 'rgba(255,255,255,0.5)',
                      }
                    }}
                  >
                    {tab.label}
                  </Button>
                );
              })}
            </Box>

            {/* New Measurement Button */}
            <Button
              variant="contained"
              startIcon={
                creatingSnapshot ? (
                  <CircularProgress size="0.9rem" sx={{ color: amDesignColors.white }} />
                ) : snapshotSuccess ? (
                  <CheckCircle sx={{ fontSize: '0.9rem', color: amDesignColors.white }} />
                ) : (
                  <CameraAlt sx={{ fontSize: '0.9rem' }} />
                )
              }
              onClick={createSnapshot}
              disabled={analyzing || creatingSnapshot}
              sx={{
                ...amDesignComponents.button,
                px: 1.5,
                py: 0.6,
                fontSize: '0.85rem',
                whiteSpace: 'nowrap',
                backgroundColor: snapshotSuccess ? amDesignColors.success : amDesignColors.primary,
                color: amDesignColors.white,
                '&:hover': {
                  backgroundColor: snapshotSuccess ? amDesignColors.success : amDesignColors.primaryDark
                }
              }}
            >
              {creatingSnapshot ? 'Erstelle...' : snapshotSuccess ? 'Erfolgreich!' : 'Neue Messung'}
            </Button>
          </Box>
        </Box>

        <Menu
          anchorEl={simulationMenuAnchor}
          open={Boolean(simulationMenuAnchor)}
          onClose={() => setSimulationMenuAnchor(null)}
        >
          <MenuItem onClick={() => { generateSimulation(); setSimulationMenuAnchor(null); }}>
            <Analytics sx={{ mr: 1 }} />
            Daten generieren (5 Wochen)
          </MenuItem>
          <MenuItem onClick={() => { clearSimulation(); setSimulationMenuAnchor(null); }}>
            <Delete sx={{ mr: 1 }} />
            Simulation zurücksetzen
          </MenuItem>
        </Menu>
      </Box>

      {/* Main Content - Split Screen Layout */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Panel - Keywords & RegioKI */}
        <Box
          sx={{
            width: isMobile ? '100%' : '35%',
            borderRight: isMobile ? 'none' : '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
            display: isMobile && rightPanelView !== 'keywords' ? 'none' : 'block'
          }}
        >
          <Box sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Keywords Section */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <KeywordSidebar
                keywords={overview?.trackedKeywords || []}
                selectedKeyword={selectedKeyword}
                onKeywordSelect={handleKeywordSelect}
                onKeywordDelete={deleteKeyword}
                onKeywordRefresh={handleKeywordRefresh}
                onAddKeyword={() => setKeywordDialog(true)}
                onCreateMeasurement={createSnapshot}
                loading={creatingSnapshot}
                overview={overview}
              />
            </Box>

          </Box>
        </Box>

        {/* Right Panel - Map or Comparison */}
        <Box 
          sx={{ 
            flex: 1,
            overflow: 'hidden',
            display: isMobile && rightPanelView === 'keywords' ? 'none' : 'block'
          }}
        >
          <Box sx={{ p: 1, height: '100%' }}>
            {rightPanelView === 'map' && (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Metrics Cards Row */}
                {selectedKeyword && karteKeywordData && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.5, mb: 2 }}>
                    {/* Average Rank Card */}
                    <Card sx={{ ...amDesignComponents.card, borderRadius: '14px' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: amDesignColors.text.tertiary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: amDesignTypography.fontFamily.heading }}>
                          Ø-Rang.
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 1 }}>
                          <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 800, color: amDesignColors.text.primary, fontFamily: amDesignTypography.fontFamily.heading, fontVariantNumeric: 'tabular-nums' }}>
                            {karteKeywordData?.averageRank ? karteKeywordData.averageRank : 'N/A'}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: amDesignColors.primary, fontWeight: 700, fontFamily: amDesignTypography.fontFamily.heading }}>
                            gefallen
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Best Rank Card */}
                    <Card sx={{ ...amDesignComponents.card, borderRadius: '14px' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: amDesignColors.text.tertiary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: amDesignTypography.fontFamily.heading }}>
                          Bester Rang
                        </Typography>
                        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 800, color: amDesignColors.success, mt: 1, fontFamily: amDesignTypography.fontFamily.heading, fontVariantNumeric: 'tabular-nums' }}>
                          {karteKeywordData?.bestRank || karteKeywordData?.currentAvgRank ? Math.min(karteKeywordData.bestRank || karteKeywordData.currentAvgRank) : 'N/A'}
                        </Typography>
                      </CardContent>
                    </Card>

                    {/* Worst Rank Card */}
                    <Card sx={{ ...amDesignComponents.card, borderRadius: '14px' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: amDesignColors.text.tertiary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: amDesignTypography.fontFamily.heading }}>
                          Schlechtester
                        </Typography>
                        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 800, color: amDesignColors.error, mt: 1, fontFamily: amDesignTypography.fontFamily.heading, fontVariantNumeric: 'tabular-nums' }}>
                          {karteKeywordData?.worstRank || karteKeywordData?.currentAvgRank ? Math.max(karteKeywordData.worstRank || karteKeywordData.currentAvgRank) : 'N/A'}
                        </Typography>
                      </CardContent>
                    </Card>

                    {/* Measurement Points Card */}
                    <Card sx={{ ...amDesignComponents.card, borderRadius: '14px' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: amDesignColors.text.tertiary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: amDesignTypography.fontFamily.heading }}>
                          Messpunkte
                        </Typography>
                        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 800, color: amDesignColors.info, mt: 1, fontFamily: amDesignTypography.fontFamily.heading, fontVariantNumeric: 'tabular-nums' }}>
                          {karteKeywordData.testLocations.length}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                )}

                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0, fontWeight: 800, fontFamily: amDesignTypography.fontFamily.heading, color: amDesignColors.text.primary }}>
                      Ranking Karte
                    </Typography>
                  </Box>

                  {selectedKeyword && (() => {
                    const lastAnalyzed = karteKeywordData?.lastAnalyzed;

                    return lastAnalyzed ? (
                      <Chip
                        label={`Stand: ${new Date(lastAnalyzed).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          ...amDesignComponents.chip,
                          fontSize: '0.7rem',
                          height: 24,
                          borderColor: amDesignColors.border,
                          backgroundColor: amDesignColors.paper
                        }}
                      />
                    ) : (
                      <Chip
                        label="Keine Daten"
                        size="small"
                        variant="outlined"
                        sx={{
                          ...amDesignComponents.chip,
                          fontSize: '0.7rem',
                          height: 24,
                          backgroundColor: `${amDesignColors.warning}15`,
                          borderColor: amDesignColors.warning,
                          color: amDesignColors.warning
                        }}
                      />
                    );
                  })()}
                </Box>

                {/* Snapshot Date Selector */}
                {selectedKeyword && karteAvailableDates.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Snapshot Datum</InputLabel>
                      <Select
                        value={karteSnapshotDate}
                        onChange={(e) => setKarteSnapshotDate(e.target.value)}
                        label="Snapshot Datum"
                      >
                        {karteAvailableDates.map((date) => (
                          <MenuItem key={date.value} value={date.value}>
                            {date.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Ranking Range Legend */}
                <Box sx={{ ...amDesignComponents.card, display: 'flex', gap: 2, alignItems: 'center', mb: 1.5, p: 2, borderRadius: '14px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: amDesignColors.success }} />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: amDesignColors.text.secondary, fontWeight: 600 }}>
                      1-3
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: amDesignColors.warning }} />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: amDesignColors.text.secondary, fontWeight: 600 }}>
                      4-10
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: amDesignColors.error }} />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: amDesignColors.text.secondary, fontWeight: 600 }}>
                      11-20
                    </Typography>
                  </Box>
                </Box>

                {selectedKeyword ? (
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <LocationMap
                      key={`map-${selectedKeyword}-${karteSnapshotDate}`}
                      businessLocation={selectedBusiness}
                      keywords={karteKeywordData ? [karteKeywordData] : []}
                    />
                  </Box>
                ) : (
                  <Card sx={{ ...amDesignComponents.cardLarge, flex: 1, borderRadius: '14px' }}>
                    <CardContent sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center'
                    }}>
                      <Box>
                        <Map sx={{ fontSize: 64, color: amDesignColors.text.tertiary, mb: 2 }} />
                        <Typography variant="h6" sx={{ color: amDesignColors.text.secondary, fontWeight: 600 }} gutterBottom>
                          Keyword auswählen
                        </Typography>
                        <Typography variant="body2" sx={{ color: amDesignColors.text.secondary }}>
                          Wählen Sie ein Keyword aus der Liste, um die Ranking-Positionen auf der Karte zu sehen.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
            
            {rightPanelView === 'timeseries' && (
              <Box sx={{ height: '100%', overflow: 'auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0, fontWeight: 800, fontFamily: amDesignTypography.fontFamily.heading, color: amDesignColors.text.primary }}>
                      Ranking-Zeitverlauf
                    </Typography>
                  </Box>

                  {selectedKeyword && rankingHistory.length > 0 && (
                    <Chip
                      label={`${rankingHistory.length} Datenpunkte`}
                      size="small"
                      variant="outlined"
                      sx={{
                        ...amDesignComponents.chip,
                        fontSize: '0.7rem',
                        height: 24,
                        borderColor: amDesignColors.border,
                        backgroundColor: amDesignColors.paper
                      }}
                    />
                  )}
                </Box>
                
                <RankingTimeSeries
                  overview={overview}
                  selectedKeyword={selectedKeyword}
                  rankingHistory={rankingHistory}
                  onKeywordSelect={setSelectedKeyword}
                />
              </Box>
            )}

            {rightPanelView === 'comparison' && (
              <Box sx={{ height: '100%', overflow: 'auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0, fontWeight: 800, fontFamily: amDesignTypography.fontFamily.heading, color: amDesignColors.text.primary }}>
                      Ranking-Vergleich
                    </Typography>
                  </Box>

                  {selectedKeyword && rankingHistory.length > 0 && (
                    <Chip
                      label={`${rankingHistory.length} Messungen`}
                      size="small"
                      variant="outlined"
                      sx={{
                        ...amDesignComponents.chip,
                        fontSize: '0.7rem',
                        height: 24,
                        borderColor: amDesignColors.border,
                        backgroundColor: amDesignColors.paper
                      }}
                    />
                  )}
                </Box>
                
                <ComparisonMode
                  selectedKeyword={selectedKeyword}
                  rankingHistory={rankingHistory}
                  onCreateMeasurement={createSnapshot}
                  loading={creatingSnapshot}
                  success={snapshotSuccess}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Keyword Dialog */}
      <Dialog
        open={keywordDialog}
        onClose={() => setKeywordDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...amDesignComponents.card,
            borderRadius: '14px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontFamily: amDesignTypography.fontFamily.heading, color: amDesignColors.text.primary }}>
          Neues Keyword hinzufügen
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              💡 Eigenes Keyword eingeben:
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Keyword"
              fullWidth
              variant="outlined"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              placeholder="z.B. Restaurant München, Friseur Berlin, Zahnarzt Hamburg"
              helperText="Geben Sie ein beliebiges Keyword ein oder wählen Sie einen Vorschlag unten"
              sx={{ mb: 1 }}
            />
          </Box>
          
          {keywordSuggestions.length > 0 && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                🤖 Intelligente Keyword-Vorschläge:
              </Typography>
              
              {/* Primary Keywords */}
              {keywordSuggestions.filter(s => s.priority === 'primary').length > 0 && (
                <Box mb={1}>
                  <Typography variant="caption" color="primary" fontWeight="bold">
                    🎯 Priorität: Hoch
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    {keywordSuggestions
                      .filter(s => s.priority === 'primary')
                      .slice(0, 4)
                      .map((suggestion, index) => (
                        <Chip
                          key={`primary-${index}`}
                          label={typeof suggestion === 'string' ? suggestion : suggestion.keyword}
                          onClick={() => setNewKeyword(typeof suggestion === 'string' ? suggestion : suggestion.keyword)}
                          sx={{
                            ...amDesignComponents.chipPrimary,
                            '&:hover': {
                              backgroundColor: `${amDesignColors.primary}25`,
                            },
                            cursor: 'pointer'
                          }}
                          size="small"
                        />
                      ))}
                  </Box>
                </Box>
              )}
              
              {/* Secondary Keywords */}
              {keywordSuggestions.filter(s => s.priority === 'secondary').length > 0 && (
                <Box mb={1}>
                  <Typography variant="caption" color="warning.main" fontWeight="bold">
                    📈 Priorität: Mittel
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    {keywordSuggestions
                      .filter(s => s.priority === 'secondary')
                      .slice(0, 4)
                      .map((suggestion, index) => (
                        <Chip
                          key={`secondary-${index}`}
                          label={typeof suggestion === 'string' ? suggestion : suggestion.keyword}
                          onClick={() => setNewKeyword(typeof suggestion === 'string' ? suggestion : suggestion.keyword)}
                          sx={{
                            backgroundColor: `${amDesignColors.warning}15`,
                            color: amDesignColors.warning,
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            border: `1px solid ${amDesignColors.warning}40`,
                            '&:hover': {
                              backgroundColor: `${amDesignColors.warning}25`,
                            },
                            cursor: 'pointer'
                          }}
                          size="small"
                        />
                      ))}
                  </Box>
                </Box>
              )}
              
              {/* Fallback for simple string array (backwards compatibility) */}
              {keywordSuggestions.length > 0 &&
               typeof keywordSuggestions[0] === 'string' && (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {keywordSuggestions.slice(0, 8).map((suggestion, index) => (
                    <Chip
                      key={index}
                      label={suggestion}
                      onClick={() => setNewKeyword(suggestion)}
                      sx={{
                        ...amDesignComponents.chip,
                        '&:hover': {
                          backgroundColor: amDesignColors.ghost,
                          borderColor: amDesignColors.primary
                        },
                        cursor: 'pointer'
                      }}
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setKeywordDialog(false)}
            sx={{
              ...amDesignComponents.button,
              backgroundColor: amDesignColors.paper,
              border: `1px solid ${amDesignColors.border}`,
              color: amDesignColors.text.primary,
              '&:hover': {
                backgroundColor: amDesignColors.ghost,
                borderColor: amDesignColors.primary
              }
            }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => analyzeKeyword(newKeyword)}
            variant="contained"
            disabled={!newKeyword.trim() || analyzing}
            sx={{
              ...amDesignComponents.button,
              backgroundColor: amDesignColors.primary,
              color: amDesignColors.white,
              '&:hover': {
                backgroundColor: amDesignColors.primaryDark
              }
            }}
          >
            {analyzing ? <CircularProgress size={20} sx={{ color: amDesignColors.primary }} /> : 'Analysieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress Overlay */}
      <RankingProgressOverlay
        open={analyzing}
        onClose={() => {
          if (progressData?.phase === 'completed') {
            setAnalyzing(false);
            setProgressData(null);
          }
        }}
        progressData={progressData}
        keyword={progressKeyword}
      />

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.type}
          variant="filled"
          sx={{
            width: '100%',
            minWidth: '300px',
            borderRadius: '14px',
            fontWeight: 600,
            backgroundColor: toast.type === 'success' ? amDesignColors.success : toast.type === 'error' ? amDesignColors.error : amDesignColors.warning,
            color: amDesignColors.white,
            '& .MuiAlert-icon': {
              color: amDesignColors.white
            }
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default RankingDashboard;